import React from 'react';
import { motion } from 'framer-motion';
import { Users, Star, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AI_ASSISTANT, QUICK_SUGGESTIONS } from '@/lib/ai-chat/constants';
import { useGreeting } from '@/hooks/ai-chat/useGreeting';
import { useAuth } from '@/context/AuthContext';

const iconMap: Record<string, React.FC<{ className?: string }>> = {
  Users,
  Star,
  TrendingUp,
};

interface WelcomeScreenProps {
  onSuggestionClick: (text: string) => void;
}

export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ onSuggestionClick }) => {
  const { user } = useAuth();
  const { greeting, invitation, firstName } = useGreeting({
    fullName: user?.user_metadata?.full_name,
    email: user?.email,
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col items-center justify-center h-full px-4"
    >
      {/* AI Avatar - larger for welcome screen */}
      <motion.div
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        className="w-16 h-16 rounded-full flex items-center justify-center ring-2 ring-border mb-4"
      >
        <img
          src="/media/tag-innovation-logo.png"
          alt={AI_ASSISTANT.name}
          className="h-10 w-10 rounded-sm"
        />
      </motion.div>

      {/* Greeting */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="text-center mb-6"
      >
        <h2 className="text-lg font-semibold">
          {greeting}{firstName ? `, ${firstName}` : ''}!
        </h2>
        <p className="text-sm text-muted-foreground mt-1">{invitation}</p>
      </motion.div>

      {/* Quick suggestions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="w-full max-w-[280px] space-y-2"
      >
        {QUICK_SUGGESTIONS.map((suggestion, index) => {
          const Icon = iconMap[suggestion.icon];
          return (
            <motion.div
              key={suggestion.text}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
            >
              <Button
                variant="outline"
                className="w-full justify-start gap-3 h-auto py-3 px-4 text-left"
                onClick={() => onSuggestionClick(suggestion.text)}
              >
                {Icon && <Icon className={`h-4 w-4 shrink-0 ${suggestion.color}`} />}
                <span className="text-sm">{suggestion.text}</span>
              </Button>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Tagline */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="text-xs text-muted-foreground/70 mt-6 text-center"
      >
        {AI_ASSISTANT.tagline}
      </motion.p>
    </motion.div>
  );
};

export default WelcomeScreen;
